import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type { MerchantTemplate, MerchantTemplateInput } from './types';

/**
 * Merchant-template RTK Query slice: list/get/create/update/delete
 * templates, activate / clear-active, fetch system defaults, render to a
 * file. The render endpoint returns `{ fileId }` — `useRenderTemplatePreview`
 * combines that with a filesApi GET to resolve the actual URL.
 */
export const merchantTemplateApi = createApi({
  reducerPath: 'merchantTemplateApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['MerchantTemplate'],
  endpoints: (builder) => ({
    /**
     * GET /template/v1/templates/merchant/{merchantId}[?category=...] —
     * templates by merchant, optionally filtered by category. Used by both
     * the packing-slip list (no category) and the email-invitation section
     * (category=email-body-invite). The list response may include a
     * `isSystemDefault` row that has no template id — the email section
     * renders it specially.
     */
    getMerchantTemplates: builder.query<
      MerchantTemplate[],
      { merchantId: string; category?: string }
    >({
      query: ({ merchantId, category }) => {
        const qs = category ? `?category=${encodeURIComponent(category)}` : '';
        return {
          url: `${services.template}/v1/templates/merchant/${encodeURIComponent(merchantId)}${qs}`,
          method: 'GET',
        };
      },
      transformResponse: (response: MerchantTemplate[] | { templates: MerchantTemplate[] }) =>
        Array.isArray(response) ? response : (response.templates ?? []),
      providesTags: (_r, _e, { merchantId, category }) => [
        { type: 'MerchantTemplate', id: category ? `${merchantId}:${category}` : merchantId },
      ],
    }),
    /** GET /template/v1/templates/{templateId} — single template (with html/description). */
    getMerchantTemplate: builder.query<MerchantTemplate, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `${services.template}/v1/templates/${encodeURIComponent(templateId)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { templateId }) => [
        { type: 'MerchantTemplate', id: `single:${templateId}` },
      ],
    }),
    /** POST /template/v1/templates — create a new template. */
    createMerchantTemplate: builder.mutation<MerchantTemplate, MerchantTemplateInput>({
      query: (data) => ({
        url: `${services.template}/v1/templates`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'MerchantTemplate', id: arg.merchantId },
        { type: 'MerchantTemplate', id: `${arg.merchantId}:${arg.category}` },
      ],
    }),
    /** PUT /template/v1/templates/{templateId} — update an existing template. */
    updateMerchantTemplate: builder.mutation<
      MerchantTemplate,
      MerchantTemplateInput & { id: string }
    >({
      query: ({ id, ...data }) => ({
        url: `${services.template}/v1/templates/${encodeURIComponent(id)}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'MerchantTemplate', id: arg.merchantId },
        { type: 'MerchantTemplate', id: `${arg.merchantId}:${arg.category}` },
        { type: 'MerchantTemplate', id: `single:${arg.id}` },
      ],
    }),
    /**
     * POST /template/v1/templates/{templateId}/activate — mark a template as
     * the merchant's active one for its category. Only one active per
     * (merchantId, category) at a time; activating overwrites the previous
     * choice.
     */
    activateMerchantTemplate: builder.mutation<
      void,
      { templateId: string; merchantId: string; category: string }
    >({
      query: ({ templateId }) => ({
        url: `${services.template}/v1/templates/${encodeURIComponent(templateId)}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { merchantId, category }) => [
        { type: 'MerchantTemplate', id: merchantId },
        { type: 'MerchantTemplate', id: `${merchantId}:${category}` },
      ],
    }),
    /**
     * DELETE /template/v1/templates/active?merchantId=&category= — clear
     * the merchant's active template for a category so the system-default
     * is used.
     */
    clearActiveMerchantTemplate: builder.mutation<
      void,
      { merchantId: string; category: string }
    >({
      query: ({ merchantId, category }) => {
        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        qs.set('category', category);
        return {
          url: `${services.template}/v1/templates/active?${qs.toString()}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_r, _e, { merchantId, category }) => [
        { type: 'MerchantTemplate', id: merchantId },
        { type: 'MerchantTemplate', id: `${merchantId}:${category}` },
      ],
    }),
    /**
     * GET /template/v1/templates/default/{category} — fetch the system
     * default template for a category (used as the starting HTML when
     * creating a new email template).
     */
    getSystemDefaultMerchantTemplate: builder.query<MerchantTemplate, { category: string }>({
      query: ({ category }) => ({
        url: `${services.template}/v1/templates/default/${encodeURIComponent(category)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { category }) => [
        { type: 'MerchantTemplate', id: `default:${category}` },
      ],
    }),
    /** DELETE /template/v1/templates/{templateId} */
    deleteMerchantTemplate: builder.mutation<
      void,
      { merchantId: string; templateId: string; category?: string }
    >({
      query: ({ templateId }) => ({
        url: `${services.template}/v1/templates/${encodeURIComponent(templateId)}`,
        method: 'DELETE',
      }),
      // Category-scoped list queries cache under `${merchantId}:${category}`
      // (inserts card, email section) — invalidate that entry too or the
      // deleted row lingers until remount.
      invalidatesTags: (_r, _e, { merchantId, category }) => [
        { type: 'MerchantTemplate', id: merchantId },
        ...(category
          ? [{ type: 'MerchantTemplate' as const, id: `${merchantId}:${category}` }]
          : []),
      ],
    }),
    /**
     * POST /template/v1/render?templateId=X&orderId=template&paperFormat=A4&format=pdf
     * The body must include the sample-data payload (order / merchant /
     * shipment / vendor) so the template engine has something to interpolate
     * into placeholders. See `samplePreviewPayload.ts` for the canonical
     * shape ported.
     * Returns `{ fileId }`. The caller is expected to follow up with the
     * files service to resolve the actual download URL (see
     * `useRenderTemplatePreview()` in the merchant hooks folder).
     */
    renderMerchantTemplate: builder.mutation<
      { fileId: string },
      { templateId: string; samplePayload: unknown; format?: 'pdf' | 'png' }
    >({
      query: ({ templateId, samplePayload, format = 'pdf' }) => ({
        // Format is per-category: packing slips preview as the same paginated
        // PDF fulfillment produces (page breaks / print CSS visible); inserts
        // preview as PNG, matching their fulfillment artifact — and PNGs are
        // served inline, so the modal can display them.
        url: `${services.template}/v1/render?templateId=${encodeURIComponent(templateId)}&orderId=template&paperFormat=A4&format=${format}`,
        method: 'POST',
        body: samplePayload,
      }),
    }),
  }),
});

export const {
  useGetMerchantTemplatesQuery,
  useGetMerchantTemplateQuery,
  useCreateMerchantTemplateMutation,
  useUpdateMerchantTemplateMutation,
  useDeleteMerchantTemplateMutation,
  useRenderMerchantTemplateMutation,
  useActivateMerchantTemplateMutation,
  useClearActiveMerchantTemplateMutation,
  useGetSystemDefaultMerchantTemplateQuery,
} = merchantTemplateApi;
